import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Search, SlidersHorizontal, X, MapPin, ChevronDown, Heart, BookmarkPlus } from 'lucide-react';
import { listingsAPI, metaAPI, watchlistAPI, savedSearchesAPI, businessAdsAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import ListingCard from '../components/ListingCard';
import BusinessAdCard from '../components/BusinessAdCard';

const CITIES = ["Pickering", "Ajax", "Whitby", "Courtice", "Oshawa", "Bowmanville"];
const SORT_OPTIONS = [
  { value: "newest", label: "Newest First" },
  { value: "price_low", label: "Price: Low to High" },
  { value: "price_high", label: "Price: High to Low" },
  { value: "popular", label: "Most Viewed" },
];

export default function BrowsePage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [listings, setListings] = useState([]);
  const [sponsored, setSponsored] = useState([]);
  const [categories, setCategories] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [watchlistIds, setWatchlistIds] = useState([]);
  const [browseAds, setBrowseAds] = useState([]);

  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [category, setCategory] = useState(searchParams.get('category') || '');
  const [subcategory, setSubcategory] = useState(searchParams.get('subcategory') || '');
  const [city, setCity] = useState(searchParams.get('city') || '');
  const [sort, setSort] = useState(searchParams.get('sort') || 'newest');
  const [page, setPage] = useState(parseInt(searchParams.get('page') || '1'));
  const [minPrice, setMinPrice] = useState(searchParams.get('min_price') || '');
  const [maxPrice, setMaxPrice] = useState(searchParams.get('max_price') || '');

  useEffect(() => {
    metaAPI.getCategories().then(r => setCategories(r.data)).catch(() => {});
    if (user) watchlistAPI.getIds().then(r => setWatchlistIds(r.data)).catch(() => {});
    businessAdsAPI.getPublic(4).then(r => setBrowseAds(r.data || [])).catch(() => {});
  }, [user]);

  useEffect(() => {
    fetchListings();
  }, [category, subcategory, city, sort, page]);

  const fetchListings = async () => {
    setLoading(true);
    try {
      const params = { page, sort, limit: 20 };
      if (search) params.search = search;
      if (category) params.category = category;
      if (subcategory) params.subcategory = subcategory;
      if (city) params.city = city;
      if (minPrice) params.min_price = parseFloat(minPrice);
      if (maxPrice) params.max_price = parseFloat(maxPrice);

      const { data } = await listingsAPI.getAll(params);
      setListings(data.listings || []);
      setTotal(data.total || 0);
      setPages(data.pages || 0);
      // Track impressions for sponsored listings
      const sponsoredListings = data.sponsored || [];
      setSponsored(sponsoredListings);
      const promoIds = sponsoredListings.map(l => l.listing_id);
      if (promoIds.length > 0) listingsAPI.trackImpressions(promoIds).catch(() => {});
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchListings();
  };

  const clearFilters = () => {
    setSearch(''); setCategory(''); setSubcategory(''); setCity(''); setSort('newest');
    setMinPrice(''); setMaxPrice(''); setPage(1);
    setSearchParams({});
  };

  const hasActiveFilters = category || subcategory || city || minPrice || maxPrice || search;

  const handleToggleWatchlist = async (listingId) => {
    if (!user) { toast.error('Login to save listings'); return; }
    const isInList = watchlistIds.includes(listingId);
    try {
      if (isInList) {
        await watchlistAPI.remove(listingId);
        setWatchlistIds(prev => prev.filter(id => id !== listingId));
        toast.success('Removed from watchlist');
      } else {
        await watchlistAPI.add(listingId);
        setWatchlistIds(prev => [...prev, listingId]);
        toast.success('Added to watchlist');
      }
    } catch { toast.error('Failed'); }
  };

  const handleSaveSearch = async () => {
    if (!user) { toast.error('Login to save searches'); return; }
    try {
      await savedSearchesAPI.create({ search, category, subcategory, city, min_price: minPrice ? parseFloat(minPrice) : null, max_price: maxPrice ? parseFloat(maxPrice) : null, sort });
      toast.success('Search saved! You\'ll get notified of new matches.');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Search Header */}
      <div className="bg-[#F5F5F7] border-b border-zinc-200">
        <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto py-6">
          <form onSubmit={handleSearch} className="flex gap-3" data-testid="browse-search-form">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400 w-5 h-5" />
              <input
                type="text" placeholder="Search listings..."
                value={search} onChange={e => setSearch(e.target.value)}
                className="w-full pl-12 pr-4 py-3 rounded-full bg-white border border-zinc-200 text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-[#002FA7] text-sm"
                data-testid="browse-search-input"
              />
            </div>
            <button type="submit" className="bg-[#002FA7] text-white hover:bg-[#002585] rounded-full px-6 py-3 font-bold text-sm" data-testid="browse-search-btn">Search</button>
            <button
              type="button"
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 px-4 py-3 rounded-full border border-zinc-200 bg-white hover:bg-zinc-50 text-sm font-medium text-zinc-700"
              data-testid="toggle-filters-btn"
            >
              <SlidersHorizontal className="w-4 h-4" /> Filters
            </button>
          </form>

          {/* Filters */}
          {showFilters && (
            <div className="mt-4 p-4 bg-white rounded-lg border border-zinc-200 animate-fade-in-up" data-testid="filters-panel">
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                <div>
                  <label className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-1 block">Category</label>
                  <select value={category} onChange={e => { setCategory(e.target.value); setSubcategory(''); setPage(1); }}
                    className="w-full border border-zinc-200 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-[#002FA7]"
                    data-testid="filter-category-select"
                  >
                    <option value="">All Categories</option>
                    {categories.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-1 block">Subcategory</label>
                  <select value={subcategory} onChange={e => { setSubcategory(e.target.value); setPage(1); }}
                    disabled={!category}
                    className="w-full border border-zinc-200 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-[#002FA7] disabled:opacity-50"
                    data-testid="filter-subcategory-select"
                  >
                    <option value="">All Subcategories</option>
                    {(categories.find(c => c.id === category)?.subcategories || []).map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-1 block">City</label>
                  <select value={city} onChange={e => { setCity(e.target.value); setPage(1); }}
                    className="w-full border border-zinc-200 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-[#002FA7]"
                    data-testid="filter-city-select"
                  >
                    <option value="">All Cities</option>
                    {CITIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-1 block">Min Price</label>
                  <input type="number" value={minPrice} onChange={e => setMinPrice(e.target.value)} placeholder="$0"
                    className="w-full border border-zinc-200 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-[#002FA7]"
                    data-testid="filter-min-price"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-1 block">Max Price</label>
                  <input type="number" value={maxPrice} onChange={e => setMaxPrice(e.target.value)} placeholder="$∞"
                    className="w-full border border-zinc-200 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-[#002FA7]"
                    data-testid="filter-max-price"
                  />
                </div>
              </div>
              <div className="flex items-center justify-between mt-4">
                <div className="flex items-center gap-2">
                  <label className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">Sort</label>
                  <select value={sort} onChange={e => setSort(e.target.value)}
                    className="border border-zinc-200 rounded-lg px-3 py-1.5 text-sm bg-white"
                    data-testid="filter-sort-select"
                  >
                    {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                {hasActiveFilters && (
                  <button onClick={clearFilters} className="text-sm text-[#002FA7] font-semibold hover:underline flex items-center gap-1" data-testid="clear-filters-btn">
                    <X className="w-3 h-3" /> Clear filters
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Active filter badges */}
          {hasActiveFilters && (
            <div className="flex flex-wrap gap-2 mt-3">
              {category && <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-[#002FA7]/10 text-[#002FA7]">{categories.find(c => c.id === category)?.label || category} <button onClick={() => { setCategory(''); setSubcategory(''); }} className="ml-1"><X className="w-3 h-3" /></button></span>}
              {subcategory && <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-[#002FA7]/10 text-[#002FA7]">{subcategory} <button onClick={() => setSubcategory('')} className="ml-1"><X className="w-3 h-3" /></button></span>}
              {city && <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-[#002FA7]/10 text-[#002FA7]"><MapPin className="w-3 h-3 mr-1" />{city} <button onClick={() => setCity('')} className="ml-1"><X className="w-3 h-3" /></button></span>}
            </div>
          )}
        </div>
      </div>

      {/* Results */}
      <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto py-8">
        {/* Sponsored/Featured */}
        {!loading && sponsored.length > 0 && (
          <div className="mb-8" data-testid="sponsored-section">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#002FA7] mb-3">Featured</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {sponsored.map(l => <ListingCard key={l.listing_id} listing={l} promoted />)}
            </div>
          </div>
        )}

        <div className="flex items-center justify-between mb-6">
          <p className="text-sm text-zinc-500" data-testid="results-count">{total} listing{total !== 1 ? 's' : ''} found</p>
          {user && hasActiveFilters && (
            <button onClick={handleSaveSearch} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#002FA7]/20 text-xs font-bold text-[#002FA7] hover:bg-[#002FA7]/5 transition-colors" data-testid="save-search-btn">
              <BookmarkPlus className="w-3.5 h-3.5" /> Save Search
            </button>
          )}
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="bg-zinc-100 rounded-lg aspect-[4/3]" />
                <div className="mt-3 h-4 bg-zinc-100 rounded w-3/4" />
                <div className="mt-2 h-4 bg-zinc-100 rounded w-1/2" />
              </div>
            ))}
          </div>
        ) : listings.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="listings-grid">
            {listings.map((l, idx) => (
              <React.Fragment key={l.listing_id}>
                <ListingCard listing={l} isWatchlisted={watchlistIds.includes(l.listing_id)} onToggleWatchlist={user ? handleToggleWatchlist : undefined} />
                {browseAds.length > 0 && (idx + 1) % 8 === 0 && browseAds[Math.floor(idx / 8) % browseAds.length] && (
                  <BusinessAdCard ad={browseAds[Math.floor(idx / 8) % browseAds.length]} variant="inline" />
                )}
              </React.Fragment>
            ))}
          </div>
        ) : (
          <div className="text-center py-20" data-testid="no-results">
            <p className="text-lg text-zinc-500 mb-4">No listings found</p>
            <p className="text-sm text-zinc-400 mb-6">Try adjusting your filters or search terms</p>
            <Link to="/post" className="bg-[#002FA7] text-white hover:bg-[#002585] rounded-full px-6 py-2.5 font-bold text-sm">Post a Listing</Link>
          </div>
        )}

        {/* Pagination */}
        {pages > 1 && (
          <div className="flex justify-center gap-2 mt-10" data-testid="pagination">
            {[...Array(Math.min(pages, 10))].map((_, i) => (
              <button
                key={i}
                onClick={() => setPage(i + 1)}
                className={`w-10 h-10 rounded-full text-sm font-bold transition-colors ${page === i + 1 ? 'bg-[#002FA7] text-white' : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'}`}
                data-testid={`page-btn-${i + 1}`}
              >{i + 1}</button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
